
import axios from 'axios';
import { getAuthToken, isAuthenticated } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';

// Cache for macros with expiry
let macrosCache = null;
let macrosCacheExpiry = 0;
const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes

/**
 * Get all macros with caching
 * @returns {Promise<Array>} Array of macros
 */
export const getAllMacros = async () => {
  // Check if we have valid cached data
  if (macrosCache && Date.now() < macrosCacheExpiry) {
    console.log('Returning macros from cache');
    return macrosCache;
  }

  // Check if user is authenticated before attempting to get token
  if (!isAuthenticated()) {
    console.log('User not authenticated, skipping macro fetch');
    return [];
  }

  try {
    const token = await getAuthToken();
    console.log('Fetching macros from:', `${API_BASE}/admin/macros`);
    
    const response = await axios.get(`${API_BASE}/admin/macros`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      timeout: 10000
    });

    console.log('Macros response:', response.data);
    
    // The backend returns { macros: [...] }
    if (response.data && Array.isArray(response.data.macros)) {
      // Cache the macros
      macrosCache = response.data.macros;
      macrosCacheExpiry = Date.now() + CACHE_DURATION;
      console.log('Macros cached for 10 minutes');
      return response.data.macros;
    } else {
      console.warn('Unexpected response format:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching macros:', error);
    console.error('Error details:', {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data
    });
    throw error;
  }
};

/**
 * Clear the macros cache
 * Useful when macros are updated
 */
export const clearMacrosCache = () => {
  macrosCache = null;
  macrosCacheExpiry = 0;
  console.log('Macros cache cleared');
};

/**
 * Force refresh macros by clearing cache and re-fetching
 * @returns {Promise<Array>} Array of macros
 */
export const refreshMacros = async () => {
  clearMacrosCache();
  return await getAllMacros();
};

/**
 * Create a new macro
 * @param {Object} macroData - The macro data to create
 * @returns {Promise<Object>} The created macro data
 */
export const createMacro = async (macroData) => {
  // Check if user is authenticated before attempting to get token
  if (!isAuthenticated()) {
    throw new Error('User not authenticated');
  }

  try {
    const token = await getAuthToken();
    const response = await axios.post(`${API_BASE}/admin/macros`, macroData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Clear cache after creating a new macro
    clearMacrosCache();

    // The backend returns { message: string, macro: Object }
    return response.data.macro || response.data;
  } catch (error) {
    console.error('Error creating macro:', error);
    throw error;
  }
};

/**
 * Update an existing macro
 * @param {string} macroId - The macro ID to update
 * @param {Object} macroData - The updated macro data
 * @returns {Promise<Object>} The updated macro data
 */
export const updateMacro = async (macroId, macroData) => {
  // Check if user is authenticated before attempting to get token
  if (!isAuthenticated()) {
    throw new Error('User not authenticated');
  }

  try {
    const token = await getAuthToken();
    const response = await axios.put(`${API_BASE}/admin/macros/${macroId}`, macroData, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Clear cache after updating a macro
    clearMacrosCache();

    // The backend returns { message: string, macroId: string, macro: Object }
    return response.data.macro || response.data;
  } catch (error) {
    console.error('Error updating macro:', error);
    throw error;
  }
};

/**
 * Delete a macro
 * @param {string} macroId - The macro ID to delete
 * @returns {Promise<void>}
 */
export const deleteMacro = async (macroId) => {
  // Check if user is authenticated before attempting to get token
  if (!isAuthenticated()) {
    throw new Error('User not authenticated');
  }

  try {
    const token = await getAuthToken();
    await axios.delete(`${API_BASE}/admin/macros/${macroId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Clear cache after deleting a macro
    clearMacrosCache();
  } catch (error) {
    console.error('Error deleting macro:', error);
    throw error;
  }
};

/**
 * Get a macro by ID
 * @param {string} macroId - The macro ID
 * @returns {Promise<Object|null>} The macro data or null if not found
 */
export const getMacroById = async (macroId) => {
  // Check if user is authenticated before attempting to get token
  if (!isAuthenticated()) {
    throw new Error('User not authenticated');
  }

  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/admin/macros/${macroId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // The backend returns { macro: Object }
    return response.data.macro || response.data;
  } catch (error) {
    console.error('Error fetching macro:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};
